import type { PaginatedResponse } from '@/types/api';
import type {
    CreateThematicMeetingRequest,
    EditableMeetingTemplate,
    MeetingTemplate,
    MeetingTemplateCreateRequest,
    MeetingTemplateUpdateRequest,
    OverdueEmployee,
    OverdueEmployeesParams,
    ThematicMeeting,
    ThematicMeetingAgenda,
    ThematicMeetingAgendaGenerateRequest,
    ThematicMeetingAgendaUpdateRequest,
    ThematicMeetingCalendarLinks,
    ThematicMeetingListItem,
    ThematicMeetingAiGenerationResponse,
    ThematicMeetingListParams,
    ThematicMeetingStats,
    ThematicMeetingTranscript,
    UpdateThematicMeetingScheduleRequest,
} from '@/types/thematic-meetings';

import { api } from '@/api/api';

export default {
    async getThematicMeetings(
        params: ThematicMeetingListParams,
    ): Promise<PaginatedResponse<ThematicMeetingListItem>> {
        const response = await api.get('/ThematicMeeting', { params });
        return response.data;
    },

    async getThematicMeeting(id: string): Promise<ThematicMeeting> {
        const response = await api.get(`/ThematicMeeting/${id}`);
        return response.data;
    },

    async getThematicMeetingPlan(id: string): Promise<MeetingTemplate> {
        const response = await api.get(`/ThematicMeeting/${id}/Template`);
        return response.data;
    },

    async getStats(): Promise<ThematicMeetingStats> {
        const response = await api.get('/ThematicMeeting/Stats');
        return response.data;
    },

    async getOverdueEmployees(
        params: OverdueEmployeesParams,
    ): Promise<PaginatedResponse<OverdueEmployee>> {
        const response = await api.get('/ThematicMeeting/OverdueEmployees', { params });
        return response.data;
    },

    async getEditableTemplates(): Promise<EditableMeetingTemplate[]> {
        const response = await api.get('/ThematicMeeting/Templates/Manage');
        return response.data;
    },

    async getTemplates(): Promise<MeetingTemplate[]> {
        const response = await api.get('/ThematicMeeting/Templates');
        return response.data;
    },

    async getTemplateCatalog(): Promise<MeetingTemplate[]> {
        const response = await api.get('/ThematicMeeting/Templates/Catalog');
        return response.data;
    },

    async createTemplate(request: MeetingTemplateCreateRequest): Promise<EditableMeetingTemplate> {
        const response = await api.post('/ThematicMeeting/Templates', request);
        return response.data;
    },

    async importTemplate(catalogTemplateId: string): Promise<EditableMeetingTemplate> {
        const response = await api.post('/ThematicMeeting/Templates/ImportTemplate', {
            meetingPlanTemplateId: catalogTemplateId,
        });
        return response.data;
    },

    async updateTemplate(
        id: string,
        request: MeetingTemplateUpdateRequest,
    ): Promise<EditableMeetingTemplate> {
        const response = await api.put(`/ThematicMeeting/Templates/${id}`, request);
        return response.data;
    },

    async create(request: CreateThematicMeetingRequest): Promise<ThematicMeeting> {
        const response = await api.post('/ThematicMeeting', request);
        return response.data;
    },

    async updateSchedule(
        id: string,
        request: UpdateThematicMeetingScheduleRequest,
    ): Promise<ThematicMeeting> {
        const response = await api.put(`/ThematicMeeting/${id}/Schedule`, request);
        return response.data;
    },

    async updateContext(id: string, context: string | null): Promise<ThematicMeeting> {
        const response = await api.put(`/ThematicMeeting/${id}/Context`, { context });
        return response.data;
    },

    async updatePreparationNotes(
        id: string,
        preparationNotes: string | null,
    ): Promise<ThematicMeeting> {
        const response = await api.put(`/ThematicMeeting/${id}/PreparationNotes`, {
            preparationNotes,
        });
        return response.data;
    },

    async remove(id: string): Promise<void> {
        await api.delete(`/ThematicMeeting/${id}`);
    },

    async complete(id: string): Promise<ThematicMeeting> {
        const response = await api.patch(`/ThematicMeeting/${id}/Complete`);
        return response.data;
    },

    async reopen(id: string): Promise<ThematicMeeting> {
        const response = await api.patch(`/ThematicMeeting/${id}/Reopen`);
        return response.data;
    },

    async updateAgenda(
        id: string,
        request: ThematicMeetingAgendaUpdateRequest,
    ): Promise<ThematicMeetingAgenda> {
        const response = await api.put(`/ThematicMeeting/${id}/Agenda`, request);
        return response.data;
    },

    async updateTitle(id: string, title: string): Promise<ThematicMeeting> {
        const response = await api.put(`/ThematicMeeting/${id}/Title`, { title });
        return response.data;
    },

    // Starts — or attaches to — the generation of the agenda. Nothing is written: the generated agenda
    // arrives over SignalR under the correlation id this reports, for the page to add or replace.
    async generateAgenda(
        id: string,
        request: ThematicMeetingAgendaGenerateRequest,
    ): Promise<ThematicMeetingAiGenerationResponse> {
        const response = await api.post(`/ThematicMeeting/${id}/Agenda/Generate`, request);
        return response.data;
    },

    // Absence of a transcript comes back as a 200 carrying a null file name, not as a 404: the server
    // reserves 404 for a caller with no business here. Nothing to translate, and no red error in the
    // console on the many meetings that carry no transcript.
    async getTranscript(id: string): Promise<ThematicMeetingTranscript> {
        const response = await api.get(`/ThematicMeeting/${id}/Transcript`);
        return response.data;
    },

    async uploadTranscript(id: string, file: File): Promise<ThematicMeetingTranscript> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/ThematicMeeting/${id}/Transcript`, formData);
        return response.data;
    },

    // Starts — or attaches to — the recap generation. The result itself arrives over SignalR under the
    // correlation id, so this only reports which one to listen on.
    async generateRecap(
        id: string,
        correlationId: string,
    ): Promise<ThematicMeetingAiGenerationResponse> {
        const response = await api.post(`/ThematicMeeting/${id}/Recap`, { correlationId });
        return response.data;
    },

    // `startAt` dates the invitation of a meeting that has no date yet; the server ignores it otherwise.
    async getCalendar(id: string, startAt?: string): Promise<Blob> {
        const response = await api.get<Blob>(`/ThematicMeeting/${id}/Calendar`, {
            responseType: 'blob',
            params: startAt ? { startAt } : undefined,
        });
        return response.data;
    },

    // Same event as `getCalendar` downloads; a meeting without a date gets links with no times.
    async getCalendarLinks(id: string): Promise<ThematicMeetingCalendarLinks> {
        const response = await api.get(`/ThematicMeeting/${id}/CalendarLinks`);
        return response.data;
    },
};
